// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.
import { useState } from 'react'
import clsx from 'clsx'
import { useTurnstile } from 'lib/owner-notes/turnstile'
import {
  OWNER_REPORT_REASONS,
  OWNER_REPORT_REASON_LABELS,
  type OwnerReportReason,
} from 'lib/dealhunt/types'
import styles from './note-card.module.css'

export type NoteReportHandler = (
  noteId: string,
  reason: OwnerReportReason,
  botToken: () => Promise<string>
) => Promise<void>

interface NoteReportProps {
  noteId: string
  /** Owned by the host (it mints a device first when needed, with this control's bot check). */
  onReport: NoteReportHandler
  /** Added to the row and to the open panel — a host that lays the control out its own way. */
  className?: string
}

/**
 * "Report" on one owner note: the link, then the reasons inline (the host grows), the reason
 * being sent, any Cloudflare challenge and the error — all right under the note being reported
 * (2026-09-11). Lifted out of `OwnerNoteCard` unchanged so the community thread reports the
 * same way; the markup and classes are the card's.
 */
const NoteReport: React.VFC<NoteReportProps> = ({ noteId, onReport, className }) => {
  const [reportOpen, setReportOpen] = useState(false)
  const [reported, setReported] = useState(false)
  const [reportError, setReportError] = useState<string | null>(null)
  /** The reason being sent — the panel says what it is sending while the check runs. */
  const [sending, setSending] = useState<OwnerReportReason | null>(null)
  // The control's own bot check (a first report from a browser without a device mints one):
  // an interactive challenge renders here, under the reason — not at the bottom of the
  // section, away from the note being reported (2026-09-11).
  const turnstile = useTurnstile()

  const report = async (reason: OwnerReportReason): Promise<void> => {
    if (sending) {
      return
    }
    setSending(reason)
    setReportError(null)
    try {
      await onReport(noteId, reason, turnstile.getToken)
      setReported(true)
      setReportOpen(false)
    } catch (e) {
      setReportError(e instanceof Error ? e.message : 'Could not send the report.')
    } finally {
      setSending(null)
    }
  }

  if (!reportOpen) {
    return (
      <div className={clsx(styles.reportRow, className)}>
        {reported ? (
          <span className={styles.fine} role="status">
            {'Reported — thanks, a moderator will look.'}
          </span>
        ) : (
          <button
            type="button"
            className={styles.reportBtn}
            aria-expanded={false}
            onClick={() => setReportOpen(true)}
          >
            {'Report'}
          </button>
        )}
      </div>
    )
  }
  if (reported) {
    return null
  }
  return (
    // Inline in the host (it grows), not a popover spilling over the next section.
    <div
      className={clsx(styles.reportPanel, className)}
      role="group"
      aria-label="Report this owner review"
    >
      <div className={styles.reportHead}>
        <p className={styles.reportTitle}>
          {sending
            ? `Reporting: ${OWNER_REPORT_REASON_LABELS[sending]}`
            : 'What’s wrong with this owner review?'}
        </p>
        {!sending && (
          <button
            type="button"
            className={styles.reportBtn}
            onClick={() => {
              setReportOpen(false)
              setReportError(null)
            }}
          >
            {'Cancel'}
          </button>
        )}
      </div>
      {sending ? (
        <p className={styles.fine} role="status">
          {'Sending your report — Cloudflare may ask you to confirm you’re human.'}
        </p>
      ) : (
        <div className={styles.reportReasons}>
          {OWNER_REPORT_REASONS.map(reason => (
            <button
              key={reason}
              type="button"
              className={styles.reportItem}
              onClick={() => void report(reason)}
            >
              {OWNER_REPORT_REASON_LABELS[reason]}
            </button>
          ))}
        </div>
      )}
      <div ref={turnstile.containerRef} className={styles.turnstile} />
      {reportError && (
        <p className={styles.error} role="alert">
          {reportError}
        </p>
      )}
    </div>
  )
}

export default NoteReport
